import { useEffect } from "react";
import Newsletter from "../components/Newsletter";
import useCanonical from "../lib/useCanonical";

const LAST_UPDATED = "May 2026";

export default function Privacy() {
  useCanonical("/privacy");
  useEffect(() => {
    document.title = "Privacy Policy — The Tea List";
    return () => {
      document.title = "The Tea List — Australia's independent tea journal";
    };
  }, []);

  return (
    <div className="relative z-10" data-testid="privacy-page">
      <article className="max-w-3xl mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16">
        <p className="overline mb-3">The Tea List</p>
        <h1 className="headline-xl text-5xl md:text-6xl">Privacy Policy</h1>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
          Last updated · {LAST_UPDATED}
        </p>

        <div className="mt-12 prose-article max-w-2xl text-lg leading-relaxed space-y-6">
          <p>
            The Tea List ("we", "us", "our") is a small, independent
            editorial blog. This Privacy Policy explains what information we
            collect when you use thetealist.com and our APIs, why we collect
            it, how we use it, and the choices you have. We've tried to write
            it the way we'd want one written to us — plainly and without
            tricks.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">1. Who runs the site</h2>
          <p>
            The Tea List is operated by Sameera, a small family in Melbourne,
            Australia. You can reach us any time at{" "}
            <a
              href="mailto:sameera@thetealist.com"
              className="text-[#1e4a35] underline decoration-dotted underline-offset-4"
            >
              sameera@thetealist.com
            </a>
            .
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">2. What we collect</h2>
          <p>We collect only the information needed to run the site:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Newsletter signups.</strong> If you subscribe to our
              dispatch, we store your email address. Nothing else.
            </li>
            <li>
              <strong>Comments.</strong> If you leave a comment on a post, we
              store the name, email and comment text you submit. Your name and
              comment are public; your email is not displayed.
            </li>
            <li>
              <strong>Admin login.</strong> The site's editor logs in with an
              email and password. Passwords are stored only as bcrypt hashes;
              we never see or store your plaintext password.
            </li>
            <li>
              <strong>Affiliate-link click tracking.</strong> When you click
              an affiliate or sponsored link, we record the destination URL,
              the post you clicked from, the referrer header, your browser's
              user-agent, and a one-way salted hash of your IP address (we
              never store the IP itself). This lets us see which articles
              drive interest, without identifying you personally.
            </li>
            <li>
              <strong>Server logs.</strong> Like every web server, ours
              records standard request information (path, status, timestamp,
              IP) for short-term debugging and abuse prevention.
            </li>
          </ul>
          <p>
            We use <strong>Google Analytics 4</strong> — and only if you
            click "Accept" on our cookie banner. It helps us see which
            posts resonate (pageviews, referrers, broad location) without
            identifying individual readers. IP addresses are anonymised and
            no advertising cookies are set. If you click "Reject", no
            analytics scripts load and nothing is sent to Google.
            Opt out any time by clearing your site data in the browser, or
            by enabling "Do Not Track".
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">3. Cookies</h2>
          <p>
            We use a single cookie — <code>access_token</code> — for the
            site editor's login session. It is HttpOnly, Secure, and
            SameSite=Lax. If you accept analytics on the cookie banner,
            Google Analytics 4 will also set its own first-party cookies
            (typically <code>_ga</code> and <code>_ga_*</code>) to measure
            aggregate site usage. We set no advertising, remarketing or
            cross-site tracking cookies.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">4. How we use your information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To send you the newsletter you asked for.</li>
            <li>To display the comment you submitted.</li>
            <li>To authenticate the editor and run the back office.</li>
            <li>
              To understand which posts and links resonate with readers, in
              aggregate.
            </li>
            <li>
              To detect and prevent abuse (e.g., brute-force login attempts).
            </li>
          </ul>

          <h2 className="font-serif text-2xl mt-12 mb-2">5. We don't sell your data</h2>
          <p>
            We do not sell, rent, or trade your personal information to
            anyone, ever. If that ever changes, we will update this policy and
            give you advance notice through the newsletter.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">6. Service providers</h2>
          <p>
            To run the site we rely on a small number of standard
            infrastructure providers (cloud hosting, database, email
            delivery, image generation). They process information on our
            instructions and only as needed to provide the service. We do not
            give them permission to use your information for their own
            purposes.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">7. International users and the GDPR / Australian Privacy Act</h2>
          <p>
            We're based in Australia and our practices are aligned with the
            Australian Privacy Principles. If you are in the UK, EU, or EEA,
            you also have rights under the GDPR — including the right to
            access, correct, or delete the personal information we hold about
            you, and to object to or restrict its processing. To exercise any
            of these rights, email{" "}
            <a
              href="mailto:sameera@thetealist.com"
              className="text-[#1e4a35] underline decoration-dotted underline-offset-4"
            >
              sameera@thetealist.com
            </a>{" "}
            and we will respond within 30 days.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">8. Retention</h2>
          <p>
            We keep newsletter emails until you unsubscribe (a one-click link
            is in every email). Comments stay published unless you ask us to
            remove them. Affiliate-click records are kept for up to 24
            months, then anonymised or deleted. Server logs are kept for 30
            days.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">9. Children</h2>
          <p>
            The Tea List is not directed at children under 16 and we do not
            knowingly collect information from them. If you believe we have,
            email us and we will delete it.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">10. Security</h2>
          <p>
            Connections to the site are encrypted with TLS. Login cookies are
            HttpOnly and Secure. Passwords are bcrypt-hashed. We employ
            defence-in-depth measures including security response headers and
            brute-force protection. No system is perfectly secure, but we
            take reasonable precautions and treat the small amount of data we
            hold with care.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">11. External links</h2>
          <p>
            Articles on this site link to third-party websites — tea
            estates, retailers, research papers, news pieces. Their privacy
            practices are their own, not ours. We encourage you to read their
            policies before sharing personal information with them.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">12. Changes to this policy</h2>
          <p>
            If we change this policy in any meaningful way, we will update
            the "Last updated" date above and announce the change in the
            newsletter. Material changes affecting how we use existing
            personal information will not be applied retroactively without
            your consent.
          </p>

          <h2 className="font-serif text-2xl mt-12 mb-2">13. Contact</h2>
          <p>
            Questions, concerns, removal requests, or just a hello —{" "}
            <a
              href="mailto:sameera@thetealist.com"
              className="text-[#1e4a35] underline decoration-dotted underline-offset-4"
            >
              sameera@thetealist.com
            </a>
            .
          </p>
        </div>
      </article>

      <Newsletter />
    </div>
  );
}
